import { useContext, useRef, useState, useEffect } from 'react';
import './login.css';
import Footer from '../../components/footer/Footer';
import { useConfettiText } from '../../hooks/useConfettiText';
import { loginCall } from '../../apiCalls';
import { AuthContext } from '../../context/AuthContext';
import { CircularProgress } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import SpotifyIframeNormalBlack from '../../utils/spotifyIframe';
import { ViewPortContext } from '../../context/ViewPortContext';
import { useLocation, useHistory } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import GenericHelmet from '../../components/genericHelmet/GenericHelmet'; // import your generic helmet
import TrafficConstellationsCanvas from '../../components/traffic/TrafficConstellationsCanvas';
import { socket, getLatencyMs } from '../../context/socket';

const LOGIN_PARTICLE_COUNT = 100;
// Normalize continuation pointer to jg:returnTo (returnTo wins over legacy)
const normalizeReturnTo = (search = '') => {
  const params = new URLSearchParams(search);
  const returnTo = params.get('returnTo');
  const redirect = params.get('redirect');
  const from = params.get('from');
  const candidate = returnTo || redirect || from || null;
  if (candidate && typeof window !== 'undefined') {
    try {
      localStorage.setItem('jg:returnTo', candidate);
    } catch (err) {
      // ignore storage failures
    }
  }
  return candidate;
};
const upgradeSocketToAuthed = (token) => {
  if (!token || !socket) return;
  socket.auth = socket.auth || {};
  socket.auth.token = token;
  if (socket.auth.guest) delete socket.auth.guest;
  try {
    socket.emit('presence:heartbeat', { activityType: 'active', authed: true });
  } catch (err) {
    // ignore
  }
};

export default function Login() {
  const [loginSuccess, setLoginSuccess] = useState(false);

  const emailAddress = useRef();
  const password = useRef();
  const { isFetching, dispatch, player: authedPlayer } = useContext(AuthContext);
  const { height } = useContext(ViewPortContext);
  // console.log(height);
  //const { error } = useContext(AuthContext);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const history = useHistory();
  const [logoUrl, setLogoUrl] = useState('');
  const [logoTagline, setLogoTagline] = useState('');
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const redirectParam = query.get('redirect');
  let from = query.get('from') || redirectParam;
  const normalizedReturnTo = normalizeReturnTo(location.search);
  const fromRef = useRef(null);
  const { darkTheme } = useTheme();
  const localOpsRedirectLabel = redirectParam?.includes('/tasks/')
    ? 'task'
    : redirectParam?.includes('/offerings/')
    ? 'offering'
    : 'item';
  const registerHref = redirectParam
    ? `/register/1?redirect=${encodeURIComponent(redirectParam)}`
    : '/register/1';

  // console.log('location.pathname: ', location.pathname);
  const desktopView = useMediaQuery({
    query: '(min-width: 805px)',
  });
  const mobileView = useMediaQuery({
    query: '(max-width: 805px)',
  });
  const geoPatchRef = useRef({});

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    inputRef,
    mobileView,
    '+2 JGK Welcome!',
    LOGIN_PARTICLE_COUNT, // Particle count for the confetti
    'login', // Parent component name
    false, // isVoiceMemo
  );
  const [showPassword, setShowPassword] = useState(false); // toggle for password visibility

  const isMounted = useRef(true);
  useEffect(() => {
    if (!loginSuccess && !authedPlayer?.token) return;
    const token = authedPlayer?.token;
    if (token) upgradeSocketToAuthed(token);
    const resume = localStorage.getItem('jg:resume');
    if (resume) {
      history.replace('/playermap');
      return;
    }
    const rt = localStorage.getItem('jg:returnTo');
    const storedFrom = localStorage.getItem('from');
    const legacyTarget =
      typeof from === 'string' && from !== '/login' && from !== 'null'
        ? from
        : storedFrom;
    const isValidPath = (val) =>
      typeof val === 'string' &&
      val.startsWith('/') &&
      val !== '/login' &&
      val !== 'null' &&
      val !== 'undefined';

    if (rt) {
      history.replace(rt);
      return;
    }
    if (isValidPath(legacyTarget)) {
      history.replace(legacyTarget);
    }
  }, [loginSuccess, authedPlayer?.token, history, from]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  //console.log(desktopView);
  useEffect(() => {
    // If already authenticated, skip guest wiring
    if (authedPlayer?.token) return undefined;

    let heartbeatId = null;
    let recovering = false;

    const params = new URLSearchParams(window.location.search);
    const pickParam = (key) => params.get(key) || null;
    const locale = navigator.language || '';
    const localeRegion =
      (locale.includes('-') && locale.split('-')[1]) ||
      (locale.includes('_') && locale.split('_')[1]) ||
      null;

    const guestAuth = {
      guest: 'true',
      utm_source: pickParam('utm_source'),
      utm_medium: pickParam('utm_medium'),
      utm_campaign: pickParam('utm_campaign'),
      utm_content: pickParam('utm_content'),
      region: pickParam('region') || localeRegion,
      referrer: document.referrer || null, // avoid self-URL fallback to reduce self-attribution
    };

    Object.keys(guestAuth).forEach((k) => {
      if (guestAuth[k] === null || guestAuth[k] === '') delete guestAuth[k];
    });

    // Persist attribution so reconnects and post-login sessions carry source data.
    const attribForStorage = { ...guestAuth };
    delete attribForStorage.guest;
    try {
      localStorage.setItem('jg_guest_attrib', JSON.stringify(attribForStorage));
    } catch (e) {
      // ignore storage errors
    }

    // Force guest mode: strip any lingering auth/token before connecting
    if (socket.auth) {
      delete socket.auth.token;
    } else {
      socket.auth = {};
    }
    const prevAuth = socket.auth || {};
    socket.auth = { ...prevAuth, ...guestAuth };
    if (!socket.connected) socket.connect();

    const emitHeartbeat = (activityType = 'active') => {
      const sid = socket.id;
      if (!sid || !socket.connected) return;
      const latencyMs = getLatencyMs?.();
      socket.emit('presence:heartbeat', {
        guest: true,
        sessionId: sid,
        activityType,
        latencyMs: Number.isFinite(latencyMs) ? Math.round(latencyMs) : null,
        ...geoPatchRef.current,
      });
    };

    const startHeartbeat = () => {
      if (heartbeatId) return;
      heartbeatId = setInterval(() => {
        if (socket.connected) {
          const vis =
            document.visibilityState === 'visible' ? 'active' : 'background';
          emitHeartbeat(vis);
        }
      }, 10_000);
      const vis =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(vis);
    };

    const stopHeartbeat = () => {
      if (heartbeatId) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };

    const handleVisibility = () => {
      const vis =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(vis);
    };
    const handleFocus = () => emitHeartbeat('active');
    const handleBlur = () => emitHeartbeat('background');
    const handleConnect = () => startHeartbeat();
    const handleDisconnect = () => stopHeartbeat();
    const handleClose = () => stopHeartbeat();
    const handleGuestError = (err) => {
      if (err?.message && err.message.includes('unknown session')) {
        stopHeartbeat();
        if (recovering) return;
        recovering = true;
        socket.disconnect();
        setTimeout(() => {
          socket.connect();
          recovering = false;
        }, 200);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', stopHeartbeat);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('guest:error', handleGuestError);
    socket.on('close', handleClose);

    handleVisibility(); // initial send
    if (socket.connected) startHeartbeat();

    const requestGeoIfGranted = () => {
      if (!navigator?.permissions || !navigator?.geolocation) return;
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state !== 'granted') return;
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude, accuracy } = pos.coords || {};
              if (
                typeof latitude === 'number' &&
                typeof longitude === 'number'
              ) {
                geoPatchRef.current = {
                  geo_lat: Number(latitude.toFixed(6)),
                  geo_lon: Number(longitude.toFixed(6)),
                  geo_accuracy: Math.round(accuracy || 0),
                };
                emitHeartbeat(
                  document.visibilityState === 'visible'
                    ? 'active'
                    : 'background',
                );
              }
            },
            () => {},
            {
              enableHighAccuracy: false,
              timeout: 4000,
              maximumAge: 60000,
            },
          );
        })
        .catch(() => {});
    };

    requestGeoIfGranted();

    return () => {
      stopHeartbeat();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', stopHeartbeat);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('guest:error', handleGuestError);
      socket.off('close', handleClose);
      if (!socket.auth?.token) socket.auth = {};
    };
  }, []);

  const handleClick = async (e) => {
    e.preventDefault();

    try {
      const result = await loginCall(
        {
          emailAddress: emailAddress.current.value,
          password: password.current.value,
        },
        dispatch,
      );

      if (isMounted.current) {
        if (result === 'success') {
          setLoginSuccess(true);
        } else {
          setError(
            result.response ? result.response.statusText : 'An error occurred',
          );
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.response ? err.response.statusText : 'An error occurred');
      }
    }
  };

  // useEffect(() => {
  //   if (loginSuccess) {
  //     // console.log('loginSuccess');
  //     showConfettiText();
  //   }
  // }, [loginSuccess]);

  useEffect(() => {
    const isValidPath = (val) =>
      typeof val === 'string' &&
      val.startsWith('/') &&
      val !== '/login' &&
      val !== 'null' &&
      val !== 'undefined';

    console.log('from', from);
    // Only persist valid "from"
    if (isValidPath(from)) {
      localStorage.setItem('from', from);
    }

    // Check if unlockedGalaxies is true
    const unlockedGalaxies =
      localStorage.getItem('unlockedGalaxies') === 'true';
    if (!unlockedGalaxies) {
      const originFrom = localStorage.getItem('originFrom');
      if (!originFrom || originFrom === 'null') {
        if (isValidPath(from)) {
          console.log(
            'there is no originFrom, or it is null, setting now to: ',
            from,
          );
          localStorage.setItem('originFrom', from);
        }
      } else {
        console.log('originFrom already set to: ', originFrom);
      }
    } else {
      console.log('unlockedGalaxies is true, not setting originFrom');
    }

    const originFrom = localStorage.getItem('originFrom');
    if (isValidPath(from)) {
      fromRef.current = from;
    } else if (isValidPath(originFrom)) {
      fromRef.current = originFrom;
    }
  }, [from]);

  useEffect(() => {
    if (
      from?.includes('/galaxies/camden-live-galaxy') ||
      from?.includes('/galaxies/65e95c2b95ff6915e2f5f361')
    ) {
      // console.log('from galaxies/65e95c2b95ff6915e2f5f361');
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1715229523/thielxhez0teywzmwycg_srujsi.jpg',
      );
      setLogoTagline('Come on down, come on down!');
    } else if (
      from?.includes('/galaxies/la-joya-eco-community') ||
      from?.includes('667de222b7513bdf52193144')
    ) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1719531368/LaJoyaLogo_cbapwt.jpg',
      );
      setLogoTagline('Adventure, Philanthropy & Prosperity are a Way of Life.');
    } else if (from?.includes('/galaxies/660a1c8149a3f75c60b457e1')) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/fl_progressive:steep/v1721518015/Images/tomq4dq5uiglbr2tcgil.png',
      );
      setLogoTagline('JGSoccer.Com Galaxy');
    } else if (
      from?.includes('/galaxies/66ef5e0d1db6c7febd8a5194') ||
      from?.includes('/galaxies/kebell-football')
    ) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1726985209/WhatsApp_Image_2024-09-22_at_00.57.28_f8e36b89_x9ufdf.jpg',
      );
      setLogoTagline('Kebell Football Galaxy');
    } else {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1658172769/Images/sbcgiu6mxdtvqvjflfwu.png',
      );
      setLogoTagline('Level Up Your Life');
    }
  }, []);

  const getResetLink = () => {
    if (emailAddress.current) {
      const emailValue = emailAddress.current.value;
      return `/reset?email=${encodeURIComponent(emailValue)}`;
    }
    return '/reset';
  };

  // console.log(from);
  return (
    <>
      <GenericHelmet
        title="Login | JOE GALAXY.NET"
        description="Log in to JOEGALAXY.NET to access your adventure profile, join group chats, and continue exploring our interactive, gamified social platform."
        image="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png"
        url="https://www.joegalaxy.net/login"
        regionName="Worldwide"
      />
      <TrafficConstellationsCanvas />
      <div className={darkTheme ? 'dark-theme blackBg login' : 'login'}>
        <div className="loginWrapper">
          <div className="loginLeft">
            <div>
              <img src={logoUrl} alt="Joe Galaxy Logo"></img>
            </div>
            {/* <h3 className="loginLogo">JOE GALAXY</h3> */}
            <span className="loginDesc">
              <p>
                {logoTagline.split(/\.\s|!\s/)[0] +
                  (logoTagline.includes('.')
                    ? '.'
                    : logoTagline.includes('!')
                    ? '!'
                    : '')}
              </p>
              <p className="tag">
                {logoTagline
                  .split(/\.\s|!\s/)
                  .slice(1)
                  .join('. ')}
              </p>
            </span>
            {/* <SpotifyIframeNormalBlack /> */}
          </div>
          <div className="loginRight">
            <div className="logoDescMobile">
              <div>
                <img src={logoUrl} alt="Joe Galaxy Logo"></img>
              </div>
              {/* <h3 className="loginLogo">JOE GALAXY</h3> */}
              <span className="loginDesc">
                <p>
                  {logoTagline.split(/\.\s|!\s/)[0] +
                    (logoTagline.includes('.')
                      ? '.'
                      : logoTagline.includes('!')
                      ? '!'
                      : '')}
                </p>
                <p className="tag">
                  {logoTagline
                    .split(/\.\s|!\s/)
                    .slice(1)
                    .join('. ')}
                </p>
              </span>
            </div>
            {redirectParam && (
              <div className="auth-redirect-banner">
                Finish signing in to view this LocalOps {localOpsRedirectLabel}.
              </div>
            )}
            <form
              className={darkTheme ? 'dark-theme blackBg loginBox' : 'loginBox'}
              onSubmit={handleClick}
            >
              <input
                placeholder="Email"
                type="email"
                required
                className="loginInput"
                ref={emailAddress}
                autoComplete="email"
              />
              <input
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength="8"
                className="loginInput"
                ref={password}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="loginInput togglePasswordBtn"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#c7cedd',
                  fontSize: 12,
                  textAlign: 'right',
                  padding: 0,
                  marginTop: 4,
                  cursor: 'pointer',
                }}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide password' : 'Show password'}
              </button>
              {/* {loginSuccess && <ConfettiTextComponent />} */}

              {error && <div className="error_text">{error}</div>}
              <button
                ref={inputRef}
                className="animated-subscribe-btn loginButton"
                type="submit"
                disabled={isFetching}
              >
                {isFetching ? (
                  <CircularProgress color="inherit" size="20px" />
                ) : (
                  'Log In'
                )}
              </button>
              <Link
                to={getResetLink()}
                className="forgotPassword"
                style={{ textDecoration: 'none' }}
                title="Joe Galaxy Password Recovery"
              >
                <span className="loginForgot">Forgot Password?</span>
              </Link>
              <div className="loginCenter">
                <Link
                  to={registerHref}
                  style={{ textDecoration: 'none' }}
                  title="Create a New Joe Galaxy Player Account"
                >
                  <button className="loginRegisterButton" disabled={isFetching}>
                    Create a New Account
                  </button>
                </Link>
              </div>
            </form>
            {/* Subtle presence note for credibility without altering layout */}
            <div
              style={{
                fontSize: 12,
                color: darkTheme ? '#9aa5b1' : '#475467',
                textAlign: 'center',
                marginTop: 12,
              }}
            >
              {/* On mobile (no footer), link to Presence Engine landing for credibility */}
              {mobileView ? (
                <a
                  href="https://presence-sdk-landing.joegalaxy.net"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  aria-label="Powered by JG Presence Engine (live)"
                >
                  Powered by JG Presence Engine (live)
                </a>
              ) : (
                'Powered by JG Presence Engine (live)'
              )}
            </div>
          </div>
        </div>
      </div>
      {!mobileView && height >= 563 && <Footer />}
    </>
  );
}
